import { useEffect, useRef, useState } from 'react';

import { cn } from '@/shared/lib/cn';
import { copiarTexto } from '@/shared/lib/copiar';

import { Icon } from './Icon';

export interface CopyButtonProps {
  /** O texto copiado, como o numero da apolice ou o CNPJ sem pontuacao. */
  value: string;
  /** Nome do que se copia, para o leitor de tela: "Copiar numero da apolice". */
  label: string;
  /** Mostra o rotulo ao lado do icone. Por padrao, so o icone. */
  showLabel?: boolean | undefined;
  className?: string | undefined;
  onCopied?: (() => void) | undefined;
}

/**
 * Botao de copiar, como o do OnPoint ao lado do numero da cotacao, da apolice e do
 * documento.
 *
 * O resultado aparece no proprio botao por dois segundos e e anunciado ao leitor de
 * tela. Falha de copia tambem aparece: um botao que nao diz que nao copiou faria a
 * pessoa colar o texto anterior sem perceber.
 */
export function CopyButton({
  value,
  label,
  showLabel,
  className,
  onCopied,
}: CopyButtonProps) {
  const [estado, setEstado] = useState<'pronto' | 'copiado' | 'falhou'>('pronto');
  const relogio = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (relogio.current) clearTimeout(relogio.current);
    },
    [],
  );

  async function copiar() {
    const deu = await copiarTexto(value);
    setEstado(deu ? 'copiado' : 'falhou');
    if (deu) onCopied?.();
    if (relogio.current) clearTimeout(relogio.current);
    relogio.current = setTimeout(() => setEstado('pronto'), 2000);
  }

  const aviso =
    estado === 'copiado' ? 'Copiado' : estado === 'falhou' ? 'Nao copiou' : '';

  return (
    <button
      type="button"
      onClick={() => void copiar()}
      disabled={value === ''}
      aria-label={showLabel ? undefined : label}
      title={label}
      className={cn(
        'inline-flex h-7 items-center gap-1 rounded-md px-1.5 text-2xs font-medium',
        'text-ink-500 transition-colors duration-(--duration-fast) hover:bg-ink-100 hover:text-ink-900',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500',
        'disabled:cursor-not-allowed disabled:opacity-50',
        estado === 'copiado' && 'text-success-700',
        estado === 'falhou' && 'text-danger-700',
        className,
      )}
    >
      <Icon name={estado === 'copiado' ? 'check' : 'copy'} size={13} />
      {showLabel ? <span>{label}</span> : null}
      <span
        role="status"
        aria-live="polite"
        className={showLabel ? undefined : 'sr-only'}
      >
        {aviso}
      </span>
    </button>
  );
}
